import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    FormContainer,
    SelectElement,
    TextFieldElement,
    useForm,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { CREATE_PRODUCT_ATTRIBUTE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductAttribute } from '@barboxx/models';
import { PRODUCT_ATTRIBUTE_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateAttributeDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<ProductAttribute.CreateRequest>({
        resolver: yupResolver(CREATE_PRODUCT_ATTRIBUTE_SCHEMA),
        defaultValues: {},
    });

    const mutateCreateAttribute = useMutation({
        mutationKey: ['attribute'],
        mutationFn: (data: ProductAttribute.CreateRequest) =>
            Api.Products.ProductAttributes.postProductAttribute(data),
    });

    const onSubmit = async (values: ProductAttribute.CreateRequest) => {
        return mutateCreateAttribute
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating attribute!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">
                Create Product Attribute
            </DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <SelectElement
                        label="Type"
                        name="attributeType"
                        options={enumToFormOptions(PRODUCT_ATTRIBUTE_TYPES)}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
